import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Select, TextField, Tooltip, Typography } from "@mui/material";
import { DataGrid, GridFooterContainer } from "@mui/x-data-grid";
import { format } from "date-fns";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import CloseIcon from "@mui/icons-material/Close";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import SouthWestIcon from "@mui/icons-material/SouthWest";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Pagination from "pages/homeDashboard/components/Pagination/Pagination";
import { useGetActiveOrganizationsQuery, useGetInProgressOrganizationsQuery } from "state/rtkQuery/org/orgQueries";
import { useGetPlatformsQuery } from "state/rtkQuery/platform/platformQueries";
import { useReviewBudgetRequestsMutation } from "state/rtkQuery/user/userMutations";
import { useGetBudgetRequestsQuery, useGetCostCenterUsersQuery, useLazyGetUserQuery } from "state/rtkQuery/user/userQueries";
import { showToast } from "utils/reusableToast";
import { useDebounce } from "utils/useDebounce";

import UserProfileButton from "components/shared/UserProfileButton/UserProfileButton";
import ReasonColumn from "../ReasonColumn/ReasonColumn";
import RequesterCell from "./RequesterCell";
import ReviewedByCell, { showReviewedByColumn } from "./ReviewedByCell";
import { UlbFilterChips, UlbRequestFilters } from "./UlbFilterPanel";
import { buildDefaultFilters, buildFilterParams } from "./ulbFilters";

export { UlbFilterChips, UlbRequestFilters };
import TableRowsSkeleton from "../TableRowsSkeleton/TableRowsSkeleton";
import BudgetUsageCell from "../CopilotDashboard/BudgetUsageCell";
import ExpandableGrid from "components/shared/ExpandableGrid/ExpandableGrid";
import UserDetailModal from "../CopilotDashboard/UserDetailModal";
import { getPresetRange } from "../CopilotDashboard/datePresets";
import { useCopilotBudgetCapabilities, useCopilotUsageCapabilities } from "components/RequirePermission/useCapabilities";

import "../AdminDataGrid/AdminDataGrid.css";

const RECORDS_PER_PAGE = 20;

// Same "always show this month" convention as JoinedTab's/AdminDashboard's
// Copilot icon — an admin reviewing a budget request wants a quick, current
// snapshot, not a date picker. Computed fresh on each open rather than once
// at module load, so a long-lived session doesn't get stuck showing a stale
// month.
const getCurrentMonthRange = () => {
  const { from, to } = getPresetRange("this_month");
  return { from: from.format("YYYY-MM-DD"), to: to.format("YYYY-MM-DD") };
};

const ACCENT = "#26890d";
const USAGE_RED = "#E2231A";
const USAGE_ORANGE = "#F28C00";

// Requests list newest-first unless the user sorts otherwise.
// Pending requests can be approved or rejected. A failed one (approved, but the GitHub apply errored)
// shows the same actions: Approve retries it, Reject abandons it.
const isActionable = (status) => status === "pending" || status === "failed";

const DEFAULT_SORT = { field: "request_date", order: "desc" };

const formatUsd = (v) =>
  "$" + Number(v ?? 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatDate = (isoDate) =>
  isoDate ? new Date(isoDate).toLocaleDateString("en-US") : "";

// GitHub Copilot budgets come in three scopes (see
// https://docs.github.com/en/copilot/concepts/billing/budgets-for-usage-based-billing):
// a per-user ULB, a cost-center budget shared by everyone in that cost
// center, or the universal (enterprise-wide) budget that applies when
// neither of the above is set. `current_budget.budget_scope` on a request
// tells us which one is *currently effective* for that user — only one ever
// applies at a time, in that priority order (ULB > cost center > universal).
// `budget_scope: "user"` means they already have their own ULB, so approving
// just adjusts it. Any other scope means they don't yet — approving will
// create a brand-new ULB for them, split off from whichever shared budget
// is currently covering them.
const isSharedScope = (scope) => Boolean(scope) && scope !== "user";

// request_id should always be present per the API contract, but some rows
// have been observed coming back without it — falling back to a composite
// key keeps the DataGrid from crashing outright (it requires every row to
// have a unique id) while that gap gets fixed server-side. user_id alone is
// NOT unique, since a user can submit multiple requests.
const getRequestRowId = (row) =>
  row.request_id ?? `${row.user_id}-${row.request_date}-${row.action}`;

const SCOPE_LABELS = {
  user: "ULB",
  multi_user_cost_center: "Cost Center Level Budget",
  multi_user_customer: "Universal Budget",
};

const scopeLabel = (scope) => SCOPE_LABELS[scope] ?? (scope ? "Shared Budget" : "—");

// Short text on the chip; the tooltip carries the fuller "… applied" wording.
const scopeAppliedTooltip = (scope) => `${scopeLabel(scope)} applied`;

// Color-codes the three budget scopes so an org owner can tell at a glance
// which one is currently effective for a user — one ULB color (their own,
// individually-managed budget) vs two shades for the shared fallbacks.
const SCOPE_CHIP_STYLES = {
  user: { color: ACCENT, bg: "rgba(38,137,13,0.1)" },
  multi_user_cost_center: { color: "#0b5fa5", bg: "rgba(11,95,165,0.1)" },
  multi_user_customer: { color: "#6a4fb3", bg: "rgba(106,79,179,0.1)" },
};

const BudgetScopeChip = ({ scope }) => {
  const { color, bg } = SCOPE_CHIP_STYLES[scope] ?? { color: "#666", bg: "rgba(0,0,0,0.06)" };
  return (
    <Tooltip title={scopeAppliedTooltip(scope)} placement="top" arrow>
      <Chip
        label={scopeLabel(scope)}
        size="small"
        sx={{ color, backgroundColor: bg, fontWeight: 700, fontSize: "0.62rem", height: "18px" }}
      />
    </Tooltip>
  );
};

const StatusChip = ({ status }) => {
  const map = {
    pending: { label: "Pending", color: "#F28C00", bg: "rgba(242,140,0,0.1)" },
    approved: { label: "Approved", color: ACCENT, bg: "rgba(38,137,13,0.1)" },
    rejected: { label: "Rejected", color: USAGE_RED, bg: "rgba(226,35,26,0.1)" },
    // Approved, but applying it to GitHub failed — an amber-red warning, distinct from a rejection.
    failed: { label: "Failed", color: "#C53211", bg: "rgba(197,50,17,0.12)" },
  };
  const { label, color, bg } = map[status] ?? map.pending;
  return <Chip label={label} size="small" sx={{ color, backgroundColor: bg, fontWeight: 700, fontSize: "0.72rem" }} />;
};

const REJECT_REASON_OPTIONS = [
  "Insufficient business justification",
  "Amount exceeds what's needed",
  "Other",
];

// `filters` / `onFiltersChange` are optionally controlled by the caller (see
// PendingApprovalsTabs.jsx, which renders <UlbRequestFilters> in its own header
// row) and fall back to internal state so this component still works standalone.
const UlbRequestsTable = ({ filters: filtersProp, onFiltersChange, refreshTick = 0 }) => {
  const userEmail = useSelector((state) => state.auth?.userAccount?.username);
  const { orgId } = useParams();
  const { data: platformData } = useGetPlatformsQuery();
  const platformId = platformData?._id;

  // Every capability below is already scoped to THIS org's role (see
  // useEffectiveOrgPermission): each checks the specific role
  // useEffectiveOrgRole(orgId) reports for this org against THAT role's own
  // permissions, not the user's global cross-org union, so a role held on a
  // DIFFERENT org can't leak in here.

  // Approving/rejecting a request updates an existing ULB OR creates a
  // brand-new one server-side (depending on whether the requester already
  // had one) even though the frontend always calls the same review/update
  // endpoint — all three permissions are required unconditionally since a
  // bulk selection can mix both outcomes.
  const {
    canCreateBudget,
    canReadBudget,
    canReadBudgetById,
    canReadBudgetRequests,
    canCreateBudgetRequest,
    canReviewBudgetRequest,
  } = useCopilotBudgetCapabilities(orgId);
  const canReviewRequests = canCreateBudget && canCreateBudgetRequest && canReviewBudgetRequest;
  const canViewRequestList = canReadBudgetRequests && canReadBudget && canReadBudgetById;

  // Copilot usage icon needs the same full usage-read bundle as every other
  // "view a user's Copilot usage" surface in the app.
  const {
    canViewUsage: canViewUsageBase,
    canViewUsageById,
    canViewModelUsage,
    canViewUsageByCostCenter,
    canViewUsageByCostCenterId,
  } = useCopilotUsageCapabilities(orgId);
  const canViewCopilotUsage =
    canViewUsageBase && canViewUsageById && canViewModelUsage && canViewUsageByCostCenter && canViewUsageByCostCenterId;

  const [internalFilters, setInternalFilters] = useState(buildDefaultFilters);
  const filters = filtersProp ?? internalFilters;
  const handleFiltersChange = onFiltersChange ?? setInternalFilters;
  const [currentPage, setCurrentPage] = useState(1);
  // 20 a page; the expanded window can show more per page.
  const [recordsPerPage, setRecordsPerPage] = useState(RECORDS_PER_PAGE);
  const changePageSize = (size) => {
    setRecordsPerPage(size);
    setCurrentPage(1);
  };
  const [sortBy, setSortBy] = useState(DEFAULT_SORT.field);
  const [sortOrder, setSortOrder] = useState(DEFAULT_SORT.order);

  // Debounced so a search isn't fired on every keystroke — matches the
  // pattern used by the OrgHistory tabs' own email search. The other filters
  // only change by picking a value, so they apply immediately.
  const debouncedEmail = useDebounce(filters.email.value, 400);
  const debouncedReviewer = useDebounce(filters.reviewed_by.value, 400);
  const {
    status: statusFilter,
    action: actionParam,
    email: emailParam,
    requestDate: requestDateParam,
    requestDateTo: requestDateToParam,
    costCenterId: costCenterParam,
    reviewedBy: reviewedByParam,
  } = buildFilterParams(
    filters,
    debouncedEmail,
    debouncedReviewer
  );

  // Newest requests first until the user picks something else. Clicking a
  // header cycles asc → desc → back to that default; the default column itself
  // just flips between desc and asc, since "no sort" isn't an option here.
  const handleSort = useCallback(
    (field) => {
      if (sortBy === field) {
        if (field === DEFAULT_SORT.field) {
          setSortOrder(sortOrder === "desc" ? "asc" : "desc");
        } else if (sortOrder === "asc") {
          setSortOrder("desc");
        } else {
          setSortBy(DEFAULT_SORT.field);
          setSortOrder(DEFAULT_SORT.order);
        }
      } else {
        setSortBy(field);
        setSortOrder(field === DEFAULT_SORT.field ? DEFAULT_SORT.order : "asc");
      }
      setCurrentPage(1);
    },
    [sortBy, sortOrder]
  );

  const renderSortIcon = useCallback(
    (field) => (
      <Tooltip
        title={
          sortBy === field
            ? sortOrder === "asc"
              ? "Click for descending"
              : field === DEFAULT_SORT.field
                ? "Click for ascending"
                : "Click to reset sort"
            : field === DEFAULT_SORT.field
              ? "Click for newest first"
              : "Click to sort ascending"
        }
        placement="top"
      >
        <IconButton
          className="sort-icon"
          size="small"
          onClick={() => handleSort(field)}
          sx={{
            position: "absolute",
            right: 0,
            padding: "2px",
            minWidth: "28px",
            flexShrink: 0,
            color: sortBy === field ? "#1976d2" : "#999",
          }}
        >
          {sortBy === field && sortOrder === "desc" ? (
            <ArrowDownwardIcon fontSize="small" />
          ) : (
            <ArrowUpwardIcon fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
    ),
    [sortBy, sortOrder, handleSort]
  );

  const {
    data: requestsData,
    isFetching: isTableLoading,
    refetch,
  } = useGetBudgetRequestsQuery(
    {
      orgId,
      platformId,
      action: actionParam,
      status: statusFilter,
      page: currentPage,
      perPage: recordsPerPage,
      email: emailParam,
      requestDate: requestDateParam,
      requestDateTo: requestDateToParam,
      costCenterId: costCenterParam,
      reviewedBy: reviewedByParam,
      sortBy,
      sortOrder,
    },
    { skip: !orgId || !platformId, refetchOnMountOrArgChange: true }
  );

  // The page's Refresh button bumps refreshTick; reload this list when it changes.
  const lastRefreshTick = useRef(refreshTick);
  useEffect(() => {
    if (refreshTick === lastRefreshTick.current) return;
    lastRefreshTick.current = refreshTick;
    refetch();
  }, [refreshTick]); // eslint-disable-line

  const pageRequests = requestsData?.requests ?? [];
  const totalCount = requestsData?.total ?? 0;
  // Only show the skeleton on a genuine first load — once any data has
  // arrived, subsequent filter/page refetches use the DataGrid's own
  // (lighter) loading overlay instead of replacing the whole table.
  const isInitialLoad = isTableLoading && requestsData === undefined;

  const nPages = Math.max(1, Math.ceil(totalCount / recordsPerPage));

  // Org/cost-center name lookups and the "view user details" flow — mirrors
  // AdminDashboard's own Profile column exactly, so org owners get the same
  // user-detail popup here as on the "Pending Approvals" tab. Active +
  // In-Progress are the only two org-level statuses this system has — a user
  // can have a joined org membership recorded against an org that's since
  // moved to (or started in) either status, so both lists are merged into
  // the lookup map rather than just Active, or the modal's Organization
  // Memberships table falls back to showing a raw org ID for that row.
  const { data: activeOrgs, isSuccess: isActiveOrgsSuccess } = useGetActiveOrganizationsQuery(
    { platformId },
    { skip: !platformId }
  );
  const { data: inProgressOrgs, isSuccess: isInProgressOrgsSuccess } = useGetInProgressOrganizationsQuery(
    { platformId },
    { skip: !platformId }
  );
  const orgIdToName = useMemo(() => {
    const orgs = [
      ...(isActiveOrgsSuccess && Array.isArray(activeOrgs) ? activeOrgs : []),
      ...(isInProgressOrgsSuccess && Array.isArray(inProgressOrgs) ? inProgressOrgs : []),
    ];
    return orgs.reduce((acc, org) => {
      acc[org._id] = org.name;
      return acc;
    }, {});
  }, [activeOrgs, isActiveOrgsSuccess, inProgressOrgs, isInProgressOrgsSuccess]);

  const { data: costCentersData, isSuccess: isCostCentersSuccess } = useGetCostCenterUsersQuery();
  const costCenterById = useMemo(() => {
    const costCenters = costCentersData?.cost_centers;
    if (!isCostCentersSuccess || !Array.isArray(costCenters)) return {};
    return costCenters.reduce((acc, cc) => {
      acc[cc._id] = cc.name;
      return acc;
    }, {});
  }, [costCentersData, isCostCentersSuccess]);

  const [triggerGetUser] = useLazyGetUserQuery();
  const [selectedUserDetails, setSelectedUserDetails] = useState(null);
  const [isUserDetailsLoading, setIsUserDetailsLoading] = useState(false);
  const [userDetailsError, setUserDetailsError] = useState("");
  const [selectedCopilotUser, setSelectedCopilotUser] = useState(null);
  // Gear button element the show/hide columns panel anchors to

  const handleOpenUserDetails = async (row) => {
    // The merged modal opens straight away on the Copilot data; the profile fills in when it arrives.
    setSelectedCopilotUser(row);
    setSelectedUserDetails(null);
    setIsUserDetailsLoading(true);
    setUserDetailsError("");
    try {
      const result = await triggerGetUser({ email: row.email, platformId });
      const users = Array.isArray(result?.data) ? result.data : [];
      const exactMatch = users.find((user) => user._id === row.user_id);
      setSelectedUserDetails(exactMatch || users[0] || null);

      if (!users.length) {
        setUserDetailsError("Could not find user details for this request.");
      }
    } catch (error) {
      setSelectedUserDetails(null);
      setUserDetailsError("Failed to load user details. Please try again.");
    } finally {
      setIsUserDetailsLoading(false);
    }
  };

  const [reviewBudgetRequests] = useReviewBudgetRequestsMutation();

  const [selectedRowIds, setSelectedRowIds] = useState([]);

  // Resets pagination/selection whenever the *effective* filter/search values
  // change — whether the change came from this component's own fallback
  // filter row or from a controlling parent (e.g. PendingApprovalsTabs
  // rendering <UlbRequestFilters> and the search box in its own header,
  // bypassing the handlers above entirely). Skips the very first render so
  // mount doesn't reset anything.
  const isFirstFilterRender = useRef(true);
  useEffect(() => {
    if (isFirstFilterRender.current) {
      isFirstFilterRender.current = false;
      return;
    }
    setCurrentPage(1);
    setSelectedRowIds([]);
  }, [actionParam, statusFilter, emailParam, requestDateParam, requestDateToParam, costCenterParam, reviewedByParam, sortBy, sortOrder]);

  const [reviewTarget, setReviewTarget] = useState(null); // { requests, decision }
  const [reviewOpen, setReviewOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [otherReason, setOtherReason] = useState("");
  const [approveReason, setApproveReason] = useState("");
  const [processing, setProcessing] = useState(false);

  // Single-row review (per-row Approve/Reject buttons) and bulk review
  // (toolbar buttons, ≥1 row selected) both open the same dialog — `requests`
  // is just a list either way, so a single-item array covers both.
  const openReview = (requests, decision) => {
    setReviewTarget({ requests, decision });
    setReviewOpen(true);
    setRejectReason("");
    setOtherReason("");
    setApproveReason("");
  };

  // Only flips `open` to false here — `reviewTarget` (which drives the
  // dialog's title/button color/label) is cleared in onExited below, once the
  // Dialog's fade-out transition finishes. Nulling it immediately caused the
  // Approve/Reject button to flash the wrong color for a frame while the
  // dialog was still fading out with reviewTarget already null.
  const closeReview = () => {
    if (processing) return;
    setReviewOpen(false);
  };

  const rejectReasonInvalid =
    reviewTarget?.decision === "reject" &&
    (rejectReason === "" || (rejectReason === "Other" && otherReason.trim() === ""));

  const confirmReview = async () => {
    if (!reviewTarget || rejectReasonInvalid) return;
    const { requests, decision } = reviewTarget;
    const finalRejectReason = rejectReason === "Other" ? otherReason.trim() : rejectReason;
    const apiDecision = decision === "approve" ? "approved" : "rejected";
    setProcessing(true);

    try {
      const result = await reviewBudgetRequests({
        platformId,
        orgId,
        reviewedBy: userEmail,
        actionReason: decision === "reject" ? finalRejectReason : approveReason.trim() || undefined,
        requests: requests.map((r) => ({ email: r.email, decision: apiDecision })),
      }).unwrap();

      const succeededCount = result?.succeeded_count ?? requests.length;
      const failedCount = result?.failed_count ?? 0;

      showToast({
        title: decision === "approve" ? "Requests approved" : "Requests rejected",
        description:
          failedCount > 0
            ? `${succeededCount} ${apiDecision}, ${failedCount} failed: ${result.failed
                .map((f) => `${f.email} (${f.error})`)
                .join("; ")}`
            : `${succeededCount} request${succeededCount === 1 ? "" : "s"} ${apiDecision}.`,
        type: failedCount > 0 ? "warning" : decision === "approve" ? "success" : "info",
        id: "ulb-review-toast",
      });
      setReviewOpen(false);
      setSelectedRowIds([]);
    } catch (error) {
      showToast({
        title: "Review failed",
        description: "We couldn't process this request. Please try again.",
        type: "error",
        id: "ulb-review-error-toast",
      });
    } finally {
      setProcessing(false);
    }
  };

  const columns = [
    {
      field: "email",
      headerName: "User",
      flex: 1.3,
      minWidth: 200,
      sortable: false,
      renderHeader: () => (
        <Box
          sx={{
            display: "flex", alignItems: "center", gap: 0.25, overflow: "hidden",
            width: "100%", position: "relative", pr: "30px",
          }}
        >
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 700, fontSize: "14.5px" }}>
            User
          </span>
          {renderSortIcon("email")}
        </Box>
      ),
      renderCell: (params) => (
        <RequesterCell
          request={params.row}
          costCenterName={costCenterById[params.row.cost_center_id]}
          costCentersLoaded={isCostCentersSuccess}
        />
      ),
    },
    // One icon opens the requester's details (profile, organizations, Copilot usage and requests), right next to the User column.
    {
      field: "profile",
      headerName: "Profile",
      flex: 0.5,
      minWidth: 80,
      align: "center",
      headerAlign: "center",
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <UserProfileButton
          userLabel={params.row.email}
          onClick={() => handleOpenUserDetails(params.row)}
          licensedOrgNames={
            canViewCopilotUsage ? (params.row.licensed_organizations ?? []).map((id) => orgIdToName[id] || id) : undefined
          }
        />
      ),
    },
    {
      field: "current_budget",
      headerName: "Current Budget",
      flex: 1.5,
      minWidth: 290,
      align: "center",
      headerAlign: "center",
      sortable: false,
      renderHeader: () => (
        <Box
          sx={{
            display: "flex", alignItems: "center", justifyContent: "center", gap: 0.25, overflow: "hidden",
            width: "100%", position: "relative", pr: "30px",
          }}
        >
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 700, fontSize: "14.5px" }}>
            Current Budget
          </span>
          {renderSortIcon("current_budget")}
        </Box>
      ),
      // Consumption bar (consumed / budget) with the existing scope chip, same cell as the
      // Copilot users page. `current_budget` carries consumed_amount and updated_at.
      renderCell: (params) => (
        <BudgetUsageCell
          budget={params.row.current_budget}
          scopeChip={<BudgetScopeChip scope={params.row.current_budget?.budget_scope} />}
        />
      ),
    },
    {
      field: "change_amount",
      headerName: "Requested",
      flex: 0.8,
      minWidth: 120,
      sortable: false,
      renderHeader: () => (
        <Box
          sx={{
            display: "flex", alignItems: "center", gap: 0.25, overflow: "hidden",
            width: "100%", position: "relative", pr: "30px",
          }}
        >
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 700, fontSize: "14.5px" }}>
            Requested
          </span>
          {renderSortIcon("change_amount")}
        </Box>
      ),
      renderCell: (params) => {
        const isIncrease = params.row.action === "increase";
        const isDecrease = params.row.action === "decrease";
        const color = isIncrease ? ACCENT : isDecrease ? USAGE_RED : "#1a1a1a";
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color }}>
            {isIncrease && <NorthEastIcon sx={{ fontSize: "1rem" }} />}
            {isDecrease && <SouthWestIcon sx={{ fontSize: "1rem" }} />}
            <Typography sx={{ fontSize: "0.83rem", fontWeight: 600, color }}>
              {formatUsd(params.row.change_amount)}
            </Typography>
          </Box>
        );
      },
    },
    {
      // Budget after this request is applied: current budget +/- the requested
      // change, so approvers can see the resulting amount at a glance.
      field: "new_budget",
      headerName: "New Budget",
      flex: 0.8,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => {
        const current = Number(params.row.current_budget?.budget_amount ?? 0);
        const change = Number(params.row.change_amount ?? 0);
        const next =
          params.row.action === "increase"
            ? current + change
            : params.row.action === "decrease"
              ? Math.max(0, current - change)
              : current;
        return <Typography sx={{ fontSize: "0.83rem", fontWeight: 600, color: "#1a1a1a" }}>{formatUsd(next)}</Typography>;
      },
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.7,
      minWidth: 100,
      align: "center",
      headerAlign: "center",
      sortable: false,
      renderCell: (params) => <StatusChip status={params.row.status} />,
    },
    // Nothing to show while filtering to Pending (no decision yet), so the column is left out there.
    showReviewedByColumn(statusFilter) && {
      field: "reviewed_by",
      headerName: "Reviewed By",
      flex: 1.4,
      minWidth: 220,
      sortable: false,
      cellClassName: "ulb-reason-cell-wrap",
      renderCell: (params) => <ReviewedByCell request={params.row} />,
    },
    {
      field: "request_date",
      headerName: "Date",
      flex: 0.8,
      minWidth: 120,
      sortable: false,
      renderHeader: () => (
        <Box
          sx={{
            display: "flex", alignItems: "center", gap: 0.25, overflow: "hidden",
            width: "100%", position: "relative", pr: "30px",
          }}
        >
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 700, fontSize: "14.5px" }}>
            Date
          </span>
          {renderSortIcon("request_date")}
        </Box>
      ),
      renderCell: (params) => <span style={{ fontSize: "12px" }}>{formatDate(params.row.request_date)}</span>,
    },
    {
      field: "request_reason",
      headerName: "Reason",
      flex: 2,
      minWidth: 240,
      sortable: false,
      cellClassName: "ulb-reason-cell-wrap",
      renderCell: (params) => <ReasonColumn params={{ row: { reason: params.row.request_reason } }} />,
    },
    // Approved / Rejected requests are already decided — nothing left to act on.
    canReviewRequests && statusFilter !== "approved" && statusFilter !== "rejected" && {
      field: "actions",
      headerName: "Actions",
      flex: 0.6,
      minWidth: 90,
      align: "center",
      headerAlign: "center",
      sortable: false,
      filterable: false,
      renderCell: (params) =>
        isActionable(params.row.status) ? (
          <Box sx={{ position: "relative", display: "flex" }}>
            <Tooltip title={params.row.status === "failed" ? "Retry approval" : "Approve"}>
              <CheckCircleOutlinedIcon
                style={{ color: "green", margin: "8px", cursor: "pointer" }}
                onClick={() => openReview([params.row], "approve")}
              />
            </Tooltip>
            <Tooltip title="Reject">
              <HighlightOffOutlinedIcon
                style={{ color: "red", margin: "8px 16px 8px 10px", cursor: "pointer" }}
                onClick={() => openReview([params.row], "reject")}
              />
            </Tooltip>
          </Box>
        ) : null,
    },
  ].filter(Boolean);

  const selectedRows = useMemo(
    () => pageRequests.filter((r) => selectedRowIds.includes(getRequestRowId(r))),
    [pageRequests, selectedRowIds]
  );

  const CustomFooter = () => (
    <GridFooterContainer
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 2,
        py: 1,
        gap: 2,
      }}
    >
      <Box />
      <Pagination nPages={nPages} currentPage={currentPage} setCurrentPage={setCurrentPage} />
    </GridFooterContainer>
  );

  const currentMonthRange = getCurrentMonthRange();

  if (!canViewRequestList) {
    return (
      <Box
        sx={{
          height: 600,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          gap: 1.5,
          border: "1px dashed #d4d4d4",
          borderRadius: "12px",
          backgroundColor: "#fafafa",
          px: 4,
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            backgroundColor: "rgba(0,0,0,0.06)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <LockOutlinedIcon sx={{ fontSize: 26, color: "#888" }} />
        </Box>
        <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#1a1a1a" }}>
          You don't have permission to view budget requests
        </Typography>
        <Typography sx={{ fontSize: "0.85rem", color: "#777", maxWidth: 380 }}>
          Ask an administrator for access, or use the sidebar to navigate to another section.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: "calc(100vh - 240px)", minHeight: 600 }}>
      {/* When the caller controls these filters (passes onFiltersChange),
          it renders <UlbRequestFilters> itself elsewhere — e.g. on the same
          line as a search box — so this fallback row only appears when used
          standalone (uncontrolled). */}
      {!onFiltersChange && (
        <Box sx={{ display: "flex", alignItems: "center", mb: 1, justifyContent: "flex-end" }}>
          <UlbRequestFilters filters={filters} onFiltersChange={handleFiltersChange} />
        </Box>
      )}
      {!onFiltersChange && <UlbFilterChips filters={filters} onFiltersChange={handleFiltersChange} />}
      <ExpandableGrid
        title="AIC Budget Requests"
        rowCount={isInitialLoad ? undefined : pageRequests.length}
        filtersSlot={<UlbRequestFilters filters={filters} onFiltersChange={handleFiltersChange} />}
        chipsSlot={<UlbFilterChips filters={filters} onFiltersChange={handleFiltersChange} />}
        pageSize={recordsPerPage}
        defaultPageSize={RECORDS_PER_PAGE}
        onPageSizeChange={changePageSize}
        pageSizes={[20, 50, 100]}
        selectedCount={selectedRows.length}
        actions={
          canReviewRequests
            ? [
                { label: "Approve selected", onClick: () => openReview(selectedRows, "approve") },
                { label: "Reject selected", onClick: () => openReview(selectedRows, "reject"), danger: true },
              ]
            : []
        }
      >
        {(gridProps) =>
      isInitialLoad ? (
        <TableRowsSkeleton columns={columns} rowCount={3} rowHeight={55} />
      ) : (
        <DataGrid
          sx={{
            "& .MuiDataGrid-columnHeaderTitle": {
              fontSize: "14.5px !important",
              fontWeight: "bold !important",
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#f6f6f6",
              maxHeight: "45px",
            },
            "& .MuiDataGrid-columnHeadersInner": {
              backgroundColor: "#f6f6f6 !important",
            },
            "& .MuiDataGrid-row": {
              minHeight: "55px !important",
              boxShadow: "none !important",
              borderBottom: "0.5px solid #d4d4d4",
            },
            "& .MuiDataGrid-virtualScroller": {
              overflowX: "auto",
            },
            "& .ulb-reason-cell-wrap": {
              whiteSpace: "normal !important",
              wordBreak: "break-word",
              py: 1,
            },
          }}
          rowHeight={40}
          getRowHeight={() => "auto"}
          getRowId={getRequestRowId}
          rows={pageRequests}
          columns={columns}
          components={{ Footer: CustomFooter, ...gridProps.components }}
          componentsProps={gridProps.componentsProps}
          disableVirtualization
          disableColumnFilter
          disableColumnMenu
          disableSelectionOnClick
          getRowSpacing={(params) => ({
            top: params.isFirstVisible ? 0 : 3,
            bottom: params.isLastVisible ? 0 : 3,
          })}
          localeText={{ noRowsLabel: "No Budget Requests" }}
          loading={isTableLoading}
          checkboxSelection={canReviewRequests}
          isRowSelectable={(params) => canReviewRequests && isActionable(params.row.status)}
          onSelectionModelChange={(selection) => setSelectedRowIds(selection)}
          selectionModel={selectedRowIds}
        />
      )
        }
      </ExpandableGrid>

      {/* Approve/reject confirmation dialog, styled to match the
          UlbRequestModal/BudgetAtRiskModal convention (accent-bordered
          title, Box/Typography layout, rounded corners) instead of the
          older plain AdminConfirmationModal look. */}
      <Dialog
        open={reviewOpen}
        onClose={closeReview}
        maxWidth="xs"
        fullWidth
        TransitionProps={{ onExited: () => setReviewTarget(null) }}
        PaperProps={{ sx: { borderRadius: "12px", overflow: "hidden" } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: `3px solid ${reviewTarget?.decision === "approve" ? ACCENT : USAGE_RED}`,
            py: 2,
            px: 3,
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#1a1a1a" }}>
            {reviewTarget?.decision === "approve"
              ? `Approve Budget Request${reviewTarget.requests.length > 1 ? "s" : ""}`
              : `Reject Budget Request${reviewTarget?.requests.length > 1 ? "s" : ""}`}
          </Typography>
          <IconButton size="small" onClick={closeReview} sx={{ color: "#555" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 2.5, "&.MuiDialogContent-root": { pt: 1.5 } }}>
          {reviewTarget && (
            <>
              <Typography sx={{ fontSize: "0.85rem", color: "#1a1a1a", mb: reviewTarget.requests.length > 1 ? 1 : 2 }}>
                {reviewTarget.requests.length === 1 ? (
                  reviewTarget.decision === "approve" ? (
                    <>
                      This will {reviewTarget.requests[0].action} {reviewTarget.requests[0].email}'s budget by{" "}
                      <strong>{formatUsd(reviewTarget.requests[0].change_amount)}</strong>.
                    </>
                  ) : (
                    `Are you sure you want to reject ${reviewTarget.requests[0].email}'s request?`
                  )
                ) : reviewTarget.decision === "approve" ? (
                  `This will approve the following ${reviewTarget.requests.length} budget requests:`
                ) : (
                  `This will reject the following ${reviewTarget.requests.length} budget requests:`
                )}
              </Typography>
              {reviewTarget.requests.length > 1 && (
                <Box sx={{ maxHeight: "12rem", overflowY: "auto", mb: 2, pr: 0.5 }}>
                  {reviewTarget.requests.map((r) => {
                    const shared = isSharedScope(r.current_budget?.budget_scope);
                    return (
                      <Box
                        key={r.email}
                        sx={{ border: "1px solid #e8e8e8", borderRadius: "6px", p: 0.8, mb: 0.6, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
                          <Typography sx={{ fontSize: "0.78rem", color: "#1a1a1a" }}>{r.email}</Typography>
                          {shared && reviewTarget.decision === "approve" && (
                            <Tooltip title={`No ULB yet — approving creates one, split off from their current ${scopeLabel(r.current_budget?.budget_scope)}.`}>
                              <WarningAmberIcon sx={{ fontSize: "0.85rem", color: USAGE_ORANGE }} />
                            </Tooltip>
                          )}
                        </Box>
                        <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: "#555" }}>
                          {formatUsd(r.change_amount)}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              )}
              {reviewTarget.decision === "approve" &&
                reviewTarget.requests.length === 1 &&
                isSharedScope(reviewTarget.requests[0].current_budget?.budget_scope) && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1,
                      mb: 2,
                      p: 1.2,
                      backgroundColor: "#fff8e1",
                      border: "1px solid #ffe082",
                      borderRadius: "8px",
                    }}
                  >
                    <WarningAmberIcon sx={{ fontSize: "1.1rem", color: "#a17f00", mt: 0.2 }} />
                    <Typography sx={{ fontSize: "0.78rem", color: "#7a6100" }}>
                      {reviewTarget.requests[0].email} doesn't have a ULB yet — approving will create one for
                      them, split off from their current{" "}
                      {scopeLabel(reviewTarget.requests[0].current_budget?.budget_scope)}.
                    </Typography>
                  </Box>
                )}
              {reviewTarget.decision === "approve" && (
                <>
                  <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: "#333", mb: 0.7 }}>
                    Reason <span style={{ color: "#999", fontWeight: 400 }}>(optional)</span>
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    rows={2}
                    placeholder="Add context for this approval…"
                    value={approveReason}
                    onChange={(e) => setApproveReason(e.target.value)}
                    sx={{ "& .MuiInputBase-input": { fontSize: "0.82rem" } }}
                  />
                </>
              )}
              {reviewTarget.decision === "reject" && (
                <>
                  <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: "#333", mb: 0.7 }}>
                    Reason for Rejection <span style={{ color: USAGE_RED }}>*</span>
                  </Typography>
                  <Select
                    fullWidth
                    size="small"
                    displayEmpty
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    renderValue={(value) => value || <span style={{ color: "#999" }}>-- Select a reason --</span>}
                    sx={{ backgroundColor: "#fff", fontSize: "0.82rem", mb: rejectReason === "Other" ? 1.5 : 0 }}
                  >
                    {REJECT_REASON_OPTIONS.map((opt) => (
                      <MenuItem key={opt} value={opt}>
                        {opt}
                      </MenuItem>
                    ))}
                  </Select>
                  {rejectReason === "Other" && (
                    <TextField
                      fullWidth
                      size="small"
                      multiline
                      rows={2}
                      placeholder="Please specify"
                      value={otherReason}
                      onChange={(e) => setOtherReason(e.target.value)}
                      sx={{ "& .MuiInputBase-input": { fontSize: "0.82rem", color: "#1a1a1a" } }}
                    />
                  )}
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #f0f0f0" }}>
          <Button onClick={closeReview} sx={{ textTransform: "none", color: "#555" }}>
            Cancel
          </Button>
          <Button
            onClick={confirmReview}
            variant="contained"
            disabled={processing || rejectReasonInvalid}
            sx={{
              textTransform: "none",
              backgroundColor: reviewTarget?.decision === "approve" ? ACCENT : USAGE_RED,
              "&:hover": { backgroundColor: reviewTarget?.decision === "approve" ? "#1e6b0a" : "#b81d15" },
            }}
          >
            {processing ? (
              <CircularProgress size={18} sx={{ color: "#fff" }} />
            ) : reviewTarget?.decision === "approve" ? (
              "Approve"
            ) : (
              "Reject"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <UserDetailModal
        open={Boolean(selectedCopilotUser)}
        onClose={() => {
          setSelectedCopilotUser(null);
          setSelectedUserDetails(null);
          setUserDetailsError("");
        }}
        user={selectedCopilotUser}
        orgId={orgId}
        platformId={platformId}
        fromDate={canViewCopilotUsage ? currentMonthRange.from : undefined}
        toDate={canViewCopilotUsage ? currentMonthRange.to : undefined}
        canViewUsage={canViewCopilotUsage}
        footerNote={canViewCopilotUsage ? "Showing current month usage only. Visit the Copilot Dashboard to view other date ranges." : undefined}
        profile={{
          user: selectedUserDetails,
          orgIdToName,
          costCenterById,
          isLoading: isUserDetailsLoading,
          error: userDetailsError,
        }}
      />
    </Box>
  );
};

export default UlbRequestsTable;
